/**
 * Tipos de `map-geo.json` — GeoJSON lon/lat (RFC 7946), consumido directamente por
 * `L.geoJSON()` sem nenhuma conversão de formato no browser.
 *
 * Substituiu o antigo `map.json`, que trazia strings `d` já projectadas para
 * unidades de viewBox. Um mapa com pan/zoom projecta em runtime — é o Leaflet que
 * o faz — pelo que o gerador passou a emitir coordenadas geográficas. O trabalho
 * pesado (contornos das bandas, simplificação, arredondamento a 3 decimais)
 * continua todo em build-time; ver DATA.md.
 */

import type { Feature, FeatureCollection, LineString, MultiPolygon, Point } from 'geojson';

/** Uma banda é "ocultação >= threshold", não um intervalo — daí serem desenhadas
 *  por ordem ascendente, cada uma por cima da anterior. */
export interface BandProps {
    threshold: number;
    label: string;
}

export interface CountryProps {
    iso3: string;
    nome: string;
}

export interface DistritoGeoProps {
    code: string;
    nome: string;
    slug: string;
}

export interface ConcelhoGeoProps {
    dtmnfr: string;
    nome: string;
    distrito: string;
    region: 'continente' | 'madeira' | 'acores';
    obscurationPct: number;
}

export interface SedeProps {
    dtmnfr: string;
    nome: string;
    distrito: string;
    region: 'continente' | 'madeira' | 'acores';
    obscurationLabel: string;
    /** Só nas 18 sedes de distrito — as únicas rotuladas desde o zoom inicial. */
    capital?: boolean;
}

/**
 * Cidades espanholas (+ Andorra) — **só rótulos**, sem interacção: apenas Portugal
 * tem cálculo por concelho. A ocultação vem do mesmo motor que os concelhos.
 */
export interface CidadeProps {
    nome: string;
    iso3: string;
    /** 1 = rotulada desde o zoom inicial; 2 = só ao aproximar. */
    tier: 1 | 2;
    eclipseKind: 'partial' | 'annular' | 'total';
    obscurationPct: number;
    obscurationLabel: string;
    peakLabel: string;
}

export interface MontesinhoProps {
    areaLabel: string;
    maxDurationSeconds: number;
    medianDurationSeconds: number;
    /**
     * A duração que a peça PUBLICA, que não é a mediana calculada.
     *
     * O gerador dá mediana 20 s e máximo 38 s para os 115,8 km² da faixa portuguesa.
     * A redacção publica 26 s — o valor que circulou e que o guia do artigo repete
     * seis vezes. Os dois podem estar certos (26 cai entre a mediana e o máximo, e
     * será a duração num ponto concreto), mas mostrar 20 no mapa enquanto o texto ao
     * lado diz 26 dava ao leitor dois números para a mesma coisa.
     *
     * Fica num campo à parte, e não a substituir `medianDurationSeconds`, porque a
     * mediana continua a ser 20 e um campo com esse nome tem de dizer a verdade.
     * Opcional: se faltar, o rótulo cai na mediana.
     */
    displayDurationSeconds?: number;
}

export interface MapGeoDoc {
    meta: {
        version: number;
        generatedAt: string;
        crs: string;
        /** Moldura da geometria — inclui a Madeira e os Açores (lon até -32°). */
        frame: { lonMin: number; lonMax: number; latMin: number; latMax: number };
        /** Moldura das bandas de ocultação, só peninsular. Ver DATA.md: as ilhas ficam
         *  sem banda por cima porque a ocultação lá é abaixo dos 90% desenhados. */
        bandsFrame: { lonMin: number; lonMax: number; latMin: number; latMax: number };
        /** Moldura do limite de visibilidade e da terra do mundo — Islândia a norte,
         *  dentro de África a sul. Mais larga que `frame` só por coincidência de
         *  valores actuais; ver DATA.md. É esta que agora manda no `maxBounds`/`minZoom`. */
        worldFrame: { lonMin: number; lonMax: number; latMin: number; latMax: number };
        coordinateDecimals: number;
    };
    thresholds: number[];
    obscurationRange: { min: number; max: number };
    bands: FeatureCollection<MultiPolygon, BandProps>;
    totality: FeatureCollection<MultiPolygon, { kind: 'totality' }>;
    /** Fronteira entre "o eclipse acontece com o Sol no céu" e "não" — o pôr-do-sol
     *  do dia, não uma banda de ocultação. Um ou mais troços abertos, não um
     *  polígono: os troços que só seguiam o bordo da grelha de amostragem já foram
     *  descartados no gerador (ver DATA.md). */
    visibilityLimit: FeatureCollection<LineString, { kind: 'visibilityLimit' }>;
    countries: FeatureCollection<MultiPolygon, CountryProps>;
    /** Silhueta única do mundo (sem fronteiras de país), só como contexto por baixo
     *  da geometria detalhada da Ibéria — ver EclipseLeafletMap.ts. */
    worldLand: FeatureCollection<MultiPolygon, { kind: 'worldLand' }>;
    distritos: FeatureCollection<MultiPolygon, DistritoGeoProps>;
    /** Os 308, continente e ilhas — é esta camada que torna o mapa clicável. */
    concelhos: FeatureCollection<MultiPolygon, ConcelhoGeoProps>;
    sedes: FeatureCollection<Point, SedeProps>;
    cidades: FeatureCollection<Point, CidadeProps>;
    montesinho: Feature<Point, MontesinhoProps> | null;
}

let inFlight: Promise<MapGeoDoc> | null = null;

/** Cache em memória — um só pedido por carregamento de página. `force-cache` porque
 *  o URL já traz `?__obsver=<release>`: por essa chave o ficheiro é imutável. */
export function fetchMapGeo(url: string): Promise<MapGeoDoc> {
    if (inFlight) return inFlight;
    inFlight = fetch(url, { cache: 'force-cache' })
        .then(res => {
            if (!res.ok) throw new Error(`eclipse2026 map-geo: HTTP ${res.status} em ${url}`);
            return res.json() as Promise<MapGeoDoc>;
        })
        .catch(err => {
            inFlight = null;
            throw err;
        });
    return inFlight;
}
